import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  GitBranch,
  CheckCircle,
  XCircle,
  Clock,
  User,
  ChevronRight,
  MessageSquare,
  Filter,
  AlertTriangle,
  Plus,
} from "lucide-react";
import { workflowsAPI } from "../api";
import clsx from "clsx";
import toast from "react-hot-toast";

const statusConfig = {
  pending: {
    label: "Pending",
    color: "bg-yellow-100 text-yellow-800",
    icon: Clock,
  },
  approved: {
    label: "Approved",
    color: "bg-green-100 text-green-800",
    icon: CheckCircle,
  },
  rejected: {
    label: "Rejected",
    color: "bg-red-100 text-red-800",
    icon: XCircle,
  },
  in_progress: {
    label: "In Progress",
    color: "bg-blue-100 text-blue-800",
    icon: GitBranch,
  },
  completed: {
    label: "Completed",
    color: "bg-green-100 text-green-800",
    icon: CheckCircle,
  },
};

const priorityConfig = {
  critical: { label: "Critical", color: "text-red-500" },
  high: { label: "High", color: "text-yellow-500" },
  medium: { label: "Medium", color: "text-blue-500" },
  low: { label: "Low", color: "text-slate-500" },
};

const WorkflowCard = ({ workflow, onApprove, onReject }) => {
  const [showDetails, setShowDetails] = useState(false);
  const status = statusConfig[workflow.status] || statusConfig.pending;
  const priority = priorityConfig[workflow.priority] || priorityConfig.medium;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <GitBranch className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">{workflow.title}</h3>
            <p className="text-sm text-slate-500 capitalize">
              {workflow.workflow_type?.replace("_", " ")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={clsx("text-xs font-medium", priority.color)}>
            {priority.label}
          </span>
          <span className={clsx("badge", status.color)}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {status.label}
          </span>
        </div>
      </div>

      {workflow.description && (
        <p className="text-sm text-slate-500 mb-4">{workflow.description}</p>
      )}

      <div className="grid grid-cols-2 gap-4 mb-4">
        {workflow.machine_name && (
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-400 mb-1">Machine</p>
            <p className="text-sm text-slate-800">{workflow.machine_name}</p>
          </div>
        )}
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-400 mb-1">Requested By</p>
          <p className="text-sm text-slate-800">
            {workflow.requested_by_name || "System"}
          </p>
        </div>
      </div>

      {workflow.data && Object.keys(workflow.data).length > 0 && (
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-sm text-blue-500 hover:text-blue-600 mb-4 flex items-center gap-1"
        >
          {showDetails ? "Hide" : "Show"} Details
          <ChevronRight
            className={clsx(
              "w-4 h-4 transition-transform",
              showDetails && "rotate-90"
            )}
          />
        </button>
      )}

      {showDetails && workflow.data && (
        <div className="mb-4 p-3 bg-slate-100 rounded-lg">
          <pre className="text-xs text-slate-600 overflow-auto">
            {JSON.stringify(workflow.data, null, 2)}
          </pre>
        </div>
      )}

      {workflow.status === "pending" && (
        <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
          <button
            onClick={() => onApprove(workflow.id)}
            className="btn btn-success btn-sm flex-1"
          >
            <CheckCircle className="w-3 h-3" />
            Approve
          </button>
          <button
            onClick={() => {
              const reason = prompt("Enter rejection reason:");
              if (reason) onReject(workflow.id, reason);
            }}
            className="btn btn-danger btn-sm flex-1"
          >
            <XCircle className="w-3 h-3" />
            Reject
          </button>
        </div>
      )}

      {workflow.comments && workflow.comments.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <p className="text-xs text-slate-400 mb-2">Activity</p>
          <div className="space-y-2">
            {workflow.comments.slice(-3).map((comment, i) => (
              <div key={i} className="text-xs text-slate-500">
                <span className="font-medium capitalize text-slate-700">
                  {comment.action}
                </span>
                {comment.comment && `: ${comment.comment}`}
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

const Workflows = () => {
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const queryClient = useQueryClient();

  const { data: workflowsData, isLoading } = useQuery({
    queryKey: ["workflows", { status: statusFilter, type: typeFilter }],
    queryFn: () =>
      workflowsAPI
        .getAll({
          status: statusFilter || undefined,
          type: typeFilter || undefined,
        })
        .then((res) => res.data),
  });

  const { data: myApprovals } = useQuery({
    queryKey: ["my-approvals"],
    queryFn: () => workflowsAPI.getMyApprovals().then((res) => res.data.data),
  });

  const approveMutation = useMutation({
    mutationFn: (id) => workflowsAPI.approve(id, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      queryClient.invalidateQueries({ queryKey: ["my-approvals"] });
      toast.success("Workflow approved");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => workflowsAPI.reject(id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      queryClient.invalidateQueries({ queryKey: ["my-approvals"] });
      toast.success("Workflow rejected");
    },
  });

  const workflows = workflowsData?.data || [];
  const pendingCount = workflows.filter((w) => w.status === "pending").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Workflows</h1>
          <p className="text-slate-500">
            Manage approvals and automated workflows
          </p>
        </div>
        <button className="btn btn-primary">
          <Plus className="w-4 h-4" />
          Create Request
        </button>
      </div>

      {/* Pending Approvals Alert */}
      {myApprovals && myApprovals.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-50 border border-yellow-200 rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <div className="flex-1">
              <p className="font-medium text-yellow-900">
                You have {myApprovals.length} pending approval
                {myApprovals.length > 1 ? "s" : ""}
              </p>
              <p className="text-sm text-yellow-700">
                Review and approve or reject these requests
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-yellow-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Pending</p>
              <p className="text-2xl font-bold text-yellow-500">
                {pendingCount}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500 opacity-50" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Approved</p>
              <p className="text-2xl font-bold text-green-500">
                {workflows.filter((w) => w.status === "approved").length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-slate-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">In Progress</p>
              <p className="text-2xl font-bold text-blue-500">
                {workflows.filter((w) => w.status === "in_progress").length}
              </p>
            </div>
            <GitBranch className="w-8 h-8 text-blue-500 opacity-50" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-slate-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Total</p>
              <p className="text-2xl font-bold text-slate-800">
                {workflows.length}
              </p>
            </div>
            <GitBranch className="w-8 h-8 text-slate-400 opacity-50" />
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="">All Types</option>
          <option value="spare_requisition">Spare Requisition</option>
          <option value="escalation">Escalation</option>
          <option value="approval">Approval</option>
        </select>
      </div>

      {/* Workflows Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-white border border-slate-200 rounded-xl p-5"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl skeleton" />
                <div className="flex-1">
                  <div className="h-5 w-32 skeleton mb-2" />
                  <div className="h-4 w-24 skeleton" />
                </div>
              </div>
              <div className="h-12 skeleton mb-4" />
              <div className="grid grid-cols-2 gap-4">
                <div className="h-16 skeleton rounded-lg" />
                <div className="h-16 skeleton rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : workflows.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {workflows.map((workflow) => (
            <WorkflowCard
              key={workflow.id}
              workflow={workflow}
              onApprove={(id) => approveMutation.mutate(id)}
              onReject={(id, reason) => rejectMutation.mutate({ id, reason })}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <GitBranch className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            No workflows
          </h3>
          <p className="text-slate-500">Workflow requests will appear here.</p>
        </div>
      )}
    </div>
  );
};

export default Workflows;
